import { useContext, useEffect, useMemo } from 'react';
import { FetchQueryOptions, useQuery, useQueryClient } from 'react-query';

import { HistoryData } from 'store/types';
import { useBoundStore } from 'store';
import { cacheKeys } from 'config';
import { UserContext } from 'contexts';
import { documentsClient } from 'clients/documents/documentsClient';
import { CompareVersionsParams } from 'clients/documents/documentsClient.types';
import { transformComparisonToHistory } from 'clients/documents/documentsClient.formatter';

export type Params = {
	slug?: string;
	importantOnly?: boolean;
};

export type VersionParams = {
	slug?: string;
	importantOnly?: boolean;
	isDraft?: boolean;
};

type Options = {
	enabled: boolean;
};

type PrefetchOptions = FetchQueryOptions & {
	enabled: boolean;
};

type DocumentHistoryParams = {
	slug?: string;
};

const defaultOptions: Partial<Options> = {
  enabled: true,
};

const defaultPrefetchOptions: Partial<PrefetchOptions> = {
  enabled: true,
};

export const usePrefetchDocument = (params: Params = {}, options: Partial<PrefetchOptions> = defaultPrefetchOptions) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const { enabled, ...prefetchOptions } = options;

    if (enabled) {
      queryClient.prefetchQuery(
        [cacheKeys.documents.getDocument, params],
        () => documentsClient.getDocument(params),
        prefetchOptions,
      );
    }
  }, [options, queryClient, params]);
};

export const useDocument = (
  params: Params = {},
  options: Partial<Options & { loadDraft?: boolean }> = defaultOptions,
) => {
  const { userPermissions } = useContext(UserContext);
  const isReviewMode = useBoundStore((state) => state.reviewMode);

  const isEnabled =
			(options.enabled ?? true) &&
			!!params.slug &&
			!!userPermissions?.view_cla;

  const {
    data: { data: document } = {},
    status,
    error,
  } = useQuery(
    [cacheKeys.documents.getDocument, params, false],
    () => documentsClient.getDocument(params, false),
    {
      enabled: isEnabled,
    },
  );

  const {
    data: { data: draftDocument } = {},
    status: draftStatus,
    error: draftError,
  } = useQuery(
    [cacheKeys.documents.getDraftDocument, params, true, isReviewMode],
    () => documentsClient.getDocument(params, true),
    {
      enabled:
						!!document &&
						!!userPermissions?.change_cla &&
						Boolean(options.loadDraft ?? !isReviewMode) &&
						!isReviewMode,
      refetchOnWindowFocus: false,
    },
  );

  return {
    status,
    error,
    document,
    draftDocument,
    draftStatus,
    draftError,
    isEnabled,
  };
};

export const useDocumentVersion = (
  params: VersionParams = {},
  options: Partial<Options> = defaultOptions,
) => {
  const { userPermissions } = useContext(UserContext);

  const isEnabled =
			(options.enabled ?? true) &&
			!!params.slug &&
			!!userPermissions?.view_cla;

  const {
    data: { data: document } = {},
    status,
    error,
    isLoading,
    isFetching,
  } = useQuery(
    [cacheKeys.documents.getDocument, params, params.isDraft || false, 'version'],
    () =>
      documentsClient.getDocument(
        { slug: params.slug, importantOnly: params.importantOnly },
        params.isDraft || false,
      ),
    {
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      staleTime: 5 * 60 * 1000,
    },
  );

  return {
    document,
    status,
    error,
    isLoading,
    isFetching,
    isEnabled,
  };
};


export const useDocumentComparison = (
  params: CompareVersionsParams,
  options: Partial<Options> = defaultOptions,
) => {
  const { userPermissions } = useContext(UserContext);

  const queryKey = useMemo(() => [
    cacheKeys.documents.compareVersions,
    params.sourceSlug,
    params.targetSlug,
    params.sourceDraft,
    params.targetDraft,
  ], [params.sourceSlug, params.targetSlug, params.sourceDraft, params.targetDraft]);

  const isEnabled = useMemo(() =>
    options.enabled &&
					!!params.sourceSlug &&
					!!params.targetSlug &&
					!!userPermissions?.view_cla,
  [options.enabled, params.sourceSlug, params.targetSlug, userPermissions?.view_cla],
  );

  const {
    data: comparisonResponse,
    status,
    error,
    isLoading,
    refetch,
  } = useQuery(
    queryKey,
    () => documentsClient.compareVersions(params),
    {
      enabled: isEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const historyData: HistoryData | undefined = useMemo(() =>
    comparisonResponse?.data ? transformComparisonToHistory(comparisonResponse.data) : undefined,
  [comparisonResponse?.data],
  );

  return {
    comparisonData: comparisonResponse?.data,
    historyData,
    status,
    error,
    isLoading,
    refetch,
  };
};

export const useDraftPublishedComparison = (
  slug: string,
  options: Partial<Options> = defaultOptions,
) => {
  const params = useMemo(() => ({
    sourceSlug: slug,
    targetSlug: slug,
    sourceDraft: false,
    targetDraft: true,
  }), [slug]);

  return useDocumentComparison(params, options);
};

export const useDocumentComparisonHistory = (
  slug: string | undefined,
  options: Partial<Options> = defaultOptions,
) => {
  const memoizedOptions = useMemo(() => ({
    ...options,
    enabled: !!slug && (options.enabled ?? true),
  }), [options, slug]);

  const comparisonResult = useDraftPublishedComparison(slug || '', memoizedOptions);

  return {
    historyData: comparisonResult.historyData,
    comparisonData: comparisonResult.comparisonData,
    status: comparisonResult.status,
    error: comparisonResult.error,
    refetch: comparisonResult.refetch,
    isLoading: comparisonResult.isLoading,
  };
};

export const useCompareDocuments = (
  sourceParams: VersionParams = {},
  candidateParams: VersionParams = {},
  options: Partial<Options> = defaultOptions,
) => {
  const sourceQuery = useDocumentVersion(sourceParams, {
    enabled: options.enabled && !!sourceParams.slug,
  });

  const candidateQuery = useDocumentVersion(candidateParams, {
    enabled: options.enabled && !!candidateParams.slug,
  });

  return {
    sourceDocument: sourceQuery.document,
    candidateDocument: candidateQuery.document,
    sourceStatus: sourceQuery.status,
    candidateStatus: candidateQuery.status,
    sourceError: sourceQuery.error,
    candidateError: candidateQuery.error,
    isLoading: sourceQuery.isLoading || candidateQuery.isLoading,
    hasError: !!sourceQuery.error || !!candidateQuery.error,
  };
};

export const useDocumentHistoryV2 = (
  params: DocumentHistoryParams = {},
  options: Partial<Options> = defaultOptions,
) => {
  const { userPermissions } = useContext(UserContext);

  const { data: { data } = {}, status, error, refetch } = useQuery(
    [cacheKeys.documents.getDocumentHistoryV2, params, false],
    () => documentsClient.getDocumentHistoryV2(params),
    {
      enabled: options.enabled && !!userPermissions?.view_cla,
    },
  );

  return {
    data,
    status,
    error,
    refetch,
  };
};